import type { FC, ReactNode } from 'react'
import type { Service } from '@/types'

import Link from 'next/link'
import { ArrowUpRight, ChefHat } from 'lucide-react'
import { MediaImage } from '@/components/generals/media-image'

interface ServiceCardProps {
    service: Service
    index: number
    headingLevel?: 'h3' | 'h4'
}

export const ServiceCard: FC<ServiceCardProps> = ({ service, index, headingLevel = 'h3' }): ReactNode => {
    const Heading = headingLevel

    return (
        <article className='reveal group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-brand-sand bg-brand-white shadow-[0_1px_0_rgba(32,32,32,0.04)] transition-[box-shadow,translate,border-color] duration-500 ease-out hover:-translate-y-1.5 hover:border-brand-gold/50 hover:shadow-[0_40px_80px_-40px_rgba(32,32,32,0.45)]'>
            <div className='relative aspect-[4/3] overflow-hidden bg-brand-sand'>
                {service.image
                    ? (
                        <MediaImage
                            src={service.image}
                            alt={service.image_alt ?? service.title}
                            sizes='(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw'
                            className='transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]'
                        />
                    )
                    : (
                        <div className='flex size-full items-center justify-center bg-brand-cream text-brand-gold'>
                            <ChefHat className='size-12' aria-hidden='true' />
                        </div>
                    )}

                <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-brand-ink/45 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100' />

                <span aria-hidden='true' className='absolute top-4 left-4 inline-flex h-9 min-w-9 items-center justify-center rounded-full bg-brand-white/90 px-3 font-heading text-base font-semibold text-brand-charcoal backdrop-blur'>
                    {String(index + 1).padStart(2, '0')}
                </span>
            </div>

            <div className='flex flex-1 flex-col p-6 sm:p-7'>
                <Heading className='font-heading text-[1.7rem] leading-tight font-semibold text-brand-charcoal'>
                    <span data-mdlfy-cms={`services:${service.id}:title`}>{service.title}</span>
                </Heading>

                <p className='mt-3 flex-1 text-[15px] leading-relaxed text-brand-stone'>
                    <span data-mdlfy-cms={`services:${service.id}:summary`}>{service.summary}</span>
                </p>

                <Link
                    href={`/services/${service.slug}`}
                    className='mt-6 inline-flex items-center gap-2 self-start text-sm font-semibold text-brand-charcoal after:absolute after:inset-0 after:rounded-[1.75rem] after:content-[""] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-brand-orange'
                >
                    <span className='relative'>
                        Learn More
                        <span className='sr-only'> about {service.title}</span>
                        <span aria-hidden='true' className='absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-brand-orange transition-transform duration-300 group-hover:scale-x-100' />
                    </span>

                    <span className='inline-flex size-8 items-center justify-center rounded-full bg-brand-orange/15 text-brand-burnt transition-colors duration-300 group-hover:bg-brand-orange group-hover:text-brand-charcoal'>
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                    </span>
                </Link>
            </div>
        </article>
    )
}
